import { RadioClient } from '../transport/radioClient';
import { ConnectionState } from '../transport/types';
import { LocationTracker, GpsStatus } from '../geo/locationTracker';
import { SessionManager } from '../storage/sessionManager';
import { FusionEngine } from '../storage/fusionEngine';
import { MapManager } from '../map/mapManager';
import { FilterEngine } from '../map/filterEngine';
import { TracerouteEngine, TracerouteStats } from '../tx/tracerouteEngine';
import { StoredPacket, RadarSession } from '../storage/db';
import { exportGeoJSON, exportCSV, exportKML } from '../storage/exporters';
import { exportDatabaseBackup, importDatabaseBackup } from '../storage/backup';
import { getStorageEstimate, formatBytes } from '../storage/persistence';
import { isMobileDevice } from './deviceDetect';
import { WakeLockManager } from '../device/wakeLockManager';
import { showToast, showConfirmDialog, showPromptDialog } from './toast';
import { FeedbackManager } from './feedback';
import { ConnectionModalController } from './connectionModalController';
import { TrafficFeedController } from './trafficFeedController';

/**
 * Controller managing UI elements, HUD indicators, filter interactions,
 * real-time traffic tables, and export triggers.
 */
export class Dashboard {
  private startTime = Date.now();
  private packetCount = 0;
  private knownRelays = new Set<number>();
  private currentTransportName = 'Radio';
  private wakeLockManager = new WakeLockManager();
  private autoWakeLockEnabled = true;
  private feedbackManager = new FeedbackManager();
  private previousRadioState: ConnectionState = 'DISCONNECTED';

  private connectionModalController: ConnectionModalController;
  private trafficFeedController: TrafficFeedController;
  private activeTracerouteNodeName: string | null = null;

  constructor(
    private radioClient: RadioClient,
    private locationTracker: LocationTracker,
    private sessionManager: SessionManager,
    private fusionEngine: FusionEngine,
    private mapManager: MapManager,
    private filterEngine: FilterEngine,
    private tracerouteEngine: TracerouteEngine
  ) {
    if (typeof localStorage !== 'undefined') {
      const storedWakeLock = localStorage.getItem('noderadar_wake_lock_enabled');
      if (storedWakeLock === 'false') {
        this.autoWakeLockEnabled = false;
      }
    }

    this.connectionModalController = new ConnectionModalController({
      radioClient: this.radioClient,
      fusionEngine: this.fusionEngine,
      getTransportName: () => this.currentTransportName
    });

    this.trafficFeedController = new TrafficFeedController({
      fusionEngine: this.fusionEngine,
      onPacketClick: (packet) => {
        if (packet.lat && packet.lon) {
          this.mapManager.setFollowLocation(false);
          const map = this.mapManager.getMap();
          if (map) {
            map.setView([packet.lat, packet.lon], 16);
            this.mapManager.showInspectionVector(packet);
          }
        }
      },
      onDrawerStateChange: () => {
        setTimeout(() => {
          this.mapManager.getMap()?.invalidateSize();
        }, 310);
      }
    });
  }

  /**
   * Initializes DOM event listeners and subscribes to subsystem events.
   */
  async init(): Promise<void> {
    this.setupHardwareControls();
    this.setupGpsListeners();
    this.setupSessionControls();
    this.setupFilterControls();
    this.setupLayerModalControls();
    this.setupDataModalControls();
    this.setupFieldErgonomicsControls();
    this.setupNodeIsolationControls();
    this.setupTracerouteControls();
    this.setupExportControls();
    this.setupBackupAndStorageControls();
    this.setupDrawerToggles();
    this.setupWakeLockControls();
    this.setupTrafficFeed();

    // Populate initial sessions and nodes
    await this.refreshSessionList();
    await this.refreshNodeSelectors();
  }

  private setupHardwareControls(): void {
    const statusText = document.getElementById('radio-status-text')!;
    const statusIndicator = document.querySelector('#radio-status-badge .status-indicator')!;
    const radioStatusBadge = document.getElementById('radio-status-badge');

    // Clicking status badge: Mobile connects directly via BLE; Desktop opens connection selection dialog
    if (radioStatusBadge) {
      radioStatusBadge.addEventListener('click', async () => {
        const state = this.radioClient.getState();
        if (state === 'CONNECTED' || state === 'SYNCING_NODEDB' || state === 'RECONNECTING') {
          this.openConnectionModal();
        } else {
          // Disconnected or Error state
          if (isMobileDevice()) {
            try {
              await this.radioClient.connect('ble');
            } catch (err) {
              showToast(`Bluetooth-Verbindung fehlgeschlagen: ${(err as Error).message}`, 'error');
            }
          } else {
            this.openConnectionModal();
          }
        }
      });
    }

    this.radioClient.on('error', (err) => {
      showToast(err.message, 'error', 5000);
    });

    this.setupConnectionModalListeners();

    this.radioClient.on('stateChange', async (state, transportName) => {
      this.currentTransportName = transportName || 'Radio';
      statusIndicator.className = 'status-indicator';

      if (state === 'CONNECTED') {
        statusIndicator.classList.add('connected');
        const count = this.radioClient.getSyncedNodesCount();
        const myInfo = this.radioClient.getMyNodeInfo();
        const nodeStr = myInfo ? ` (!${myInfo.myNodeNum.toString(16)})` : '';
        statusText.textContent = 'Connected';
        statusText.title = `${this.currentTransportName} Connected${nodeStr} [${count} Nodes]`;

        if (this.previousRadioState === 'RECONNECTING') {
          showToast('Funkverbindung wiederhergestellt!', 'success', 3000);
        }

        // Automatically prevent display sleep while connected to radio node
        if (this.autoWakeLockEnabled && (this.wakeLockManager.isSupported() || this.wakeLockManager.isFallbackSupported())) {
          await this.wakeLockManager.enable();
        }
      } else if (state === 'SYNCING_NODEDB') {
        statusIndicator.classList.add('syncing');
        const count = this.radioClient.getSyncedNodesCount();
        statusText.textContent = 'Syncing...';
        statusText.title = `Syncing NodeDB (${count} Nodes)`;

        // Keep wake lock active during initial sync
        if (this.autoWakeLockEnabled && (this.wakeLockManager.isSupported() || this.wakeLockManager.isFallbackSupported()) && !this.wakeLockManager.isActive()) {
          await this.wakeLockManager.enable();
        }
      } else if (state === 'CONNECTING') {
        statusIndicator.classList.add('syncing');
        statusText.textContent = 'Connecting...';
        statusText.title = 'Connecting to radio...';
      } else if (state === 'RECONNECTING') {
        statusIndicator.classList.add('syncing');
        const attempts = this.radioClient.getReconnectAttempts();
        const maxAttempts = this.radioClient.getMaxReconnectAttempts();
        statusText.textContent = `Reconnecting (${attempts})...`;
        statusText.title = `Versuche automatische Wiederverbindung (${attempts}/${maxAttempts})...`;

        if (this.previousRadioState !== 'RECONNECTING') {
          showToast('Funkverbindung unterbrochen – Reconnect läuft...', 'warning', 4000);
        }

        // Keep wake lock active while reconnecting
        if (this.autoWakeLockEnabled && (this.wakeLockManager.isSupported() || this.wakeLockManager.isFallbackSupported()) && !this.wakeLockManager.isActive()) {
          await this.wakeLockManager.enable();
        }
      } else if (state === 'ERROR') {
        statusIndicator.classList.add('error');
        statusText.textContent = 'Error';
        statusText.title = 'Connection error';

        // Release wake lock on error, allowing display sleep
        if (this.wakeLockManager.isDesiredActive()) {
          await this.wakeLockManager.disable();
        }
      } else {
        statusText.textContent = 'Disconnected';
        statusText.title = 'Radio disconnected';

        // Release wake lock when disconnected, allowing display sleep
        if (this.wakeLockManager.isDesiredActive()) {
          await this.wakeLockManager.disable();
        }
      }

      this.previousRadioState = state;
      this.updateConnectionModalContent();
    });

    this.radioClient.on('syncProgress', (count, complete) => {
      if (!complete) {
        statusText.textContent = count > 0 ? `Syncing (${count})` : 'Syncing...';
        statusText.title = `Syncing NodeDB: ${count} Nodes`;
      } else {
        const myInfo = this.radioClient.getMyNodeInfo();
        const nodeStr = myInfo ? ` (!${myInfo.myNodeNum.toString(16)})` : '';
        statusText.textContent = 'Connected';
        statusText.title = `Connected${nodeStr} (${count} Nodes)`;
        this.refreshNodeSelectors();
        this.mapManager.renderAllKnownNodes(this.fusionEngine.getAllKnownNodes());
      }
      this.updateConnectionModalContent();
    });
  }

  /**
   * Initializes event listeners for the connection management modal dialog.
   *
   * Why: Delegates to ConnectionModalController to keep transport switching,
   * NodeDB synchronization, and disconnect actions modular.
   */
  private setupConnectionModalListeners(): void {
    this.connectionModalController.init();
  }

  /**
   * Opens the tactical connection management modal and syncs its state.
   */
  private openConnectionModal(): void {
    this.connectionModalController.open();
  }

  /**
   * Updates the connection modal contents based on the radio client's live state.
   */
  private updateConnectionModalContent(): void {
    this.connectionModalController.update();
  }

  private setupGpsListeners(): void {
    const gpsText = document.getElementById('gps-status-text')!;
    const gpsIndicator = document.querySelector('#gps-status-badge .status-indicator')!;
    const gpsBadge = document.getElementById('gps-status-badge');

    this.locationTracker.onStatusChange((status: GpsStatus, accuracy?: number) => {
      gpsIndicator.className = 'status-indicator';
      gpsText.textContent = 'GPS';

      if (status === 'FIX_ACQUIRED') {
        gpsIndicator.classList.add('connected');
        if (gpsBadge) gpsBadge.title = `GPS Fix Acquired (±${accuracy ? Math.round(accuracy) : 0}m)`;
      } else if (status === 'SEARCHING') {
        gpsIndicator.classList.add('syncing');
        if (gpsBadge) gpsBadge.title = 'GPS: Searching for satellite fix...';
      } else if (status === 'DENIED') {
        gpsIndicator.classList.add('error');
        if (gpsBadge) gpsBadge.title = 'GPS: Permission Denied';
      } else {
        gpsIndicator.classList.add('error');
        if (gpsBadge) gpsBadge.title = 'GPS: No Fix';
      }
    });

    this.locationTracker.onLocationUpdate((snapshot) => {
      this.mapManager.updateCurrentLocation(snapshot);
    });

    const btnRecenter = document.getElementById('btn-recenter') as HTMLButtonElement | null;
    if (btnRecenter) {
      btnRecenter.addEventListener('click', () => {
        const currentlyFollowing = this.mapManager.isFollowingLocationActive();
        if (currentlyFollowing) {
          this.mapManager.setFollowLocation(false);
        } else {
          const snap = this.locationTracker.getLatestSnapshot();
          this.mapManager.setFollowLocation(true, snap || undefined);
        }
      });

      this.mapManager.onFollowLocationChange((active) => {
        if (active) {
          btnRecenter.classList.add('active');
          btnRecenter.setAttribute('aria-pressed', 'true');
          btnRecenter.title = 'GPS Verfolgung aktiv (Klick zum Deaktivieren)';
        } else {
          btnRecenter.classList.remove('active');
          btnRecenter.setAttribute('aria-pressed', 'false');
          btnRecenter.title = 'GPS Zentrieren & Verfolgen';
        }
      });
    }

    const btnFitNodes = document.getElementById('btn-fit-nodes') as HTMLButtonElement | null;
    if (btnFitNodes) {
      btnFitNodes.addEventListener('click', () => {
        this.mapManager.setFollowLocation(false);
        this.mapManager.fitNodesBounds();
      });
    }
  }

  private setupSessionControls(): void {
    const sessionSelect = document.getElementById('session-select') as HTMLSelectElement;
    const btnNew = document.getElementById('btn-new-session') as HTMLButtonElement;
    const btnDelete = document.getElementById('btn-delete-session') as HTMLButtonElement;

    sessionSelect.addEventListener('change', async () => {
      const selectedId = sessionSelect.value;
      if (selectedId) {
        await this.clearNodeIsolation();
        await this.sessionManager.switchSession(selectedId);
        await this.filterEngine.setSession(selectedId);
        this.clearTrafficFeed();
      }
    });

    btnNew.addEventListener('click', async () => {
      const defaultName = `Session ${new Date().toLocaleDateString('de-DE')}`;
      const name = await showPromptDialog(
        'Neue Session',
        'Bitte gib einen Namen für die neue Aufzeichnungs-Session ein:',
        defaultName
      );
      if (name !== null) {
        await this.clearNodeIsolation();
        const newSession = await this.sessionManager.createNewSession(name || undefined);
        await this.refreshSessionList();
        sessionSelect.value = newSession.id;
        await this.filterEngine.setSession(newSession.id);
        this.clearTrafficFeed();
        showToast(`Neue Session "${newSession.name}" aktiv`, 'success');
      }
    });

    const btnClear = document.getElementById('btn-clear-session-packets') as HTMLButtonElement | null;
    if (btnClear) {
      btnClear.addEventListener('click', async () => {
        const active = this.sessionManager.getActiveSession();
        if (!active) return;
        const confirmed = await showConfirmDialog(
          'Messpunkte leeren',
          `Möchtest du alle ${active.packetCount} getrackten Punkte aus Session "${active.name}" leeren? (NodeDB-Stationen bleiben erhalten)`,
          'Punkte leeren'
        );
        if (confirmed) {
          await this.sessionManager.clearCurrentSessionPackets();
          this.mapManager.clear();
          this.packetCount = 0;
          const metricPackets = document.getElementById('metric-packets');
          if (metricPackets) metricPackets.textContent = '0';
          const countBadge = document.getElementById('traffic-count-badge');
          if (countBadge) countBadge.textContent = '(0 Packets)';
          this.clearTrafficFeed();
          await this.refreshSessionList();
          await this.filterEngine.applyFilters();
          showToast(`Punkte aus "${active.name}" geleert`, 'info');
        }
      });
    }

    btnDelete.addEventListener('click', async () => {
      const active = this.sessionManager.getActiveSession();
      if (!active) return;
      const confirmed = await showConfirmDialog(
        'Session löschen',
        `Möchtest du die Session "${active.name}" und alle ihre gespeicherten Daten unwiderruflich löschen?`,
        'Session löschen'
      );
      if (confirmed) {
        await this.sessionManager.deleteSession(active.id);
        await this.refreshSessionList();
        await this.filterEngine.applyFilters();
        showToast(`Session "${active.name}" gelöscht`, 'warning');
      }
    });

    this.sessionManager.onSessionChange((session: RadarSession) => {
      sessionSelect.value = session.id;
    });
  }

  private async refreshSessionList(): Promise<void> {
    const sessionSelect = document.getElementById('session-select') as HTMLSelectElement;
    const sessions = await this.sessionManager.getAllSessions();
    const active = this.sessionManager.getActiveSession();

    sessionSelect.innerHTML = '';
    for (const s of sessions) {
      const opt = document.createElement('option');
      opt.value = s.id;
      opt.textContent = `${s.name} (${s.packetCount} pkts)`;
      if (active && active.id === s.id) {
        opt.selected = true;
      }
      sessionSelect.appendChild(opt);
    }
  }

  private setupFilterControls(): void {
    const snrSlider = document.getElementById('filter-snr-slider') as HTMLInputElement | null;
    const snrVal = document.getElementById('snr-slider-val');

    if (snrSlider && snrVal) {
      snrSlider.addEventListener('input', async () => {
        const val = parseInt(snrSlider.value, 10);
        snrVal.textContent = `${val} dB`;
        await this.filterEngine.setMinSnr(val);
      });
    }
  }

  /**
   * Initializes event listeners for the tactical layer and overlay configuration modal.
   *
   * Why: Unclutters the control drawer and replaces Leaflet's intrusive hover popup with a
   * high-grade tactical HUD dialog providing 3-way basemap switching, 2-way data aggregation,
   * and clean overlay toggling for nodes, heatmap, and track trail.
   */
  private setupLayerModalControls(): void {
    const backdrop = document.getElementById('layer-modal-backdrop');
    const btnOpenFloating = document.getElementById('btn-floating-layers');
    const btnClose = document.getElementById('layer-modal-btn-close');

    // Basemap buttons
    const btnBasemapDark = document.getElementById('btn-basemap-dark');
    const btnBasemapStandard = document.getElementById('btn-basemap-standard');
    const btnBasemapSatellite = document.getElementById('btn-basemap-satellite');
    const basemapButtons = [btnBasemapDark, btnBasemapStandard, btnBasemapSatellite].filter(Boolean) as HTMLElement[];

    // Aggregation buttons
    const btnHex = document.getElementById('layer-btn-hex');
    const btnRaw = document.getElementById('layer-btn-raw');

    // Overlay switches
    const toggleNodes = document.getElementById('layer-toggle-nodes') as HTMLInputElement | null;
    const toggleHeatmap = document.getElementById('layer-toggle-heatmap') as HTMLInputElement | null;
    const toggleTrail = document.getElementById('layer-toggle-trail') as HTMLInputElement | null;

    const syncModalState = () => {
      // 1. Sync active basemap
      const activeBasemap = this.mapManager.getActiveBasemap();
      for (const btn of basemapButtons) {
        const bm = btn.getAttribute('data-basemap');
        btn.classList.toggle('btn-cyan', bm === activeBasemap);
      }

      // 2. Sync aggregation mode
      const activeMode = this.mapManager.getDisplayMode();
      if (btnHex) btnHex.classList.toggle('btn-cyan', activeMode === 'hexagrid');
      if (btnRaw) btnRaw.classList.toggle('btn-cyan', activeMode === 'raw');

      // 3. Sync overlays
      if (toggleNodes) toggleNodes.checked = this.mapManager.isNodeMarkersVisible();
      if (toggleHeatmap) toggleHeatmap.checked = this.mapManager.isHeatmapVisible();
      if (toggleTrail) toggleTrail.checked = this.mapManager.isTrackVisible();
    };

    const openModal = () => {
      if (backdrop) {
        syncModalState();
        backdrop.style.display = 'flex';
      }
    };

    const closeModal = () => {
      if (backdrop) {
        backdrop.style.display = 'none';
      }
    };

    if (btnOpenFloating) {
      btnOpenFloating.addEventListener('click', openModal);
    }

    if (btnClose) {
      btnClose.addEventListener('click', closeModal);
    }

    if (backdrop) {
      backdrop.addEventListener('click', (e) => {
        if (e.target === backdrop) {
          closeModal();
        }
      });
    }

    window.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && backdrop && backdrop.style.display !== 'none') {
        closeModal();
      }
    });

    // Wire Basemap 3-button bar
    for (const btn of basemapButtons) {
      btn.addEventListener('click', () => {
        const targetBasemap = btn.getAttribute('data-basemap') as 'osm-dark' | 'osm-standard' | 'satellite';
        if (targetBasemap) {
          this.mapManager.switchBasemap(targetBasemap);
          for (const b of basemapButtons) {
            b.classList.toggle('btn-cyan', b === btn);
          }
          const label = targetBasemap === 'osm-dark' ? 'Tactical Dark' : targetBasemap === 'osm-standard' ? 'Standard' : 'Satellit';
          showToast(`Kartenstil gewechselt: ${label}`, 'info');
        }
      });
    }

    // Wire Aggregation 2-way segmented buttons
    if (btnHex) {
      btnHex.addEventListener('click', () => {
        this.mapManager.setDisplayMode('hexagrid');
        btnHex.classList.add('btn-cyan');
        if (btnRaw) btnRaw.classList.remove('btn-cyan');
        showToast('Hexagrid-Aggregation aktiv', 'info');
      });
    }

    if (btnRaw) {
      btnRaw.addEventListener('click', () => {
        this.mapManager.setDisplayMode('raw');
        btnRaw.classList.add('btn-cyan');
        if (btnHex) btnHex.classList.remove('btn-cyan');
        showToast('Raw Points aktiv', 'info');
      });
    }

    // Wire Overlays
    if (toggleNodes) {
      toggleNodes.addEventListener('change', () => {
        this.mapManager.setNodeMarkersVisible(toggleNodes.checked);
        showToast(toggleNodes.checked ? 'Bekannte Stationen eingeblendet' : 'Bekannte Stationen ausgeblendet', 'info');
      });
    }

    if (toggleHeatmap) {
      toggleHeatmap.addEventListener('change', () => {
        this.mapManager.setHeatmapVisible(toggleHeatmap.checked);
        showToast(toggleHeatmap.checked ? 'Signal-Heatmap aktiviert' : 'Signal-Heatmap deaktiviert', 'info');
      });
    }

    if (toggleTrail) {
      toggleTrail.addEventListener('change', () => {
        this.mapManager.setTrackVisible(toggleTrail.checked);
        showToast(toggleTrail.checked ? 'Streckenverlauf eingeblendet' : 'Streckenverlauf ausgeblendet', 'info');
      });
    }
  }

  /**
   * Initializes event listeners for the tactical data management and sessions modal dialog.
   *
   * Why: Consolidates sessions, GIS export, and backup/storage tools into a dedicated data dialog
   * accessible via a top HUD button, completely uncluttering the settings drawer.
   */
  private setupDataModalControls(): void {
    const backdrop = document.getElementById('data-modal-backdrop');
    const btnOpen = document.getElementById('btn-toggle-data');
    const btnClose = document.getElementById('data-modal-btn-close');

    const openModal = async () => {
      if (backdrop) {
        await this.refreshSessionList();
        await this.updateStorageQuotaDisplay();
        backdrop.style.display = 'flex';
      }
    };

    const closeModal = () => {
      if (backdrop) {
        backdrop.style.display = 'none';
      }
    };

    if (btnOpen) {
      btnOpen.addEventListener('click', openModal);
    }

    if (btnClose) {
      btnClose.addEventListener('click', closeModal);
    }

    if (backdrop) {
      backdrop.addEventListener('click', (e) => {
        if (e.target === backdrop) {
          closeModal();
        }
      });
    }

    window.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && backdrop && backdrop.style.display !== 'none') {
        closeModal();
      }
    });
  }

  /**
   * Sets up event listeners for direct node isolation from map marker popups and HUD reset badges.
   *
   * Why: Replaces bulky drawer dropdowns with a streamlined, context-driven workflow where operators
   * click any node marker directly on the tactical map to isolate its traffic or reset the filter.
   */
  private setupNodeIsolationControls(): void {
    const btnClearIsolation = document.getElementById('btn-clear-isolation');

    // Event delegation on document to catch clicks on dynamically rendered popup buttons
    document.addEventListener('click', async (e: MouseEvent) => {
      const target = (e.target as HTMLElement).closest('.btn-isolate-node') as HTMLElement | null;
      if (!target) return;

      const rawNum = target.getAttribute('data-node-num');
      const rawName = target.getAttribute('data-node-name') || 'Unbekannt';
      if (!rawNum) return;

      const nodeId = parseInt(rawNum, 10);
      if (isNaN(nodeId)) return;

      await this.isolateNode(nodeId, rawName);
    });

    if (btnClearIsolation) {
      btnClearIsolation.addEventListener('click', async () => {
        await this.clearNodeIsolation();
      });
    }
  }

  /**
   * Isolates packet filtering and map rendering to a specific node station.
   *
   * Why: Lets operators analyze solitary node propagation paths without noise
   * from neighboring repeaters, centering the viewport on the transmitter and showing a clear HUD badge.
   *
   * @param nodeId - 32-bit numeric station ID to isolate
   * @param displayName - Human-readable station name or callsign for the HUD indicator
   */
  private async isolateNode(nodeId: number, displayName: string): Promise<void> {
    await this.filterEngine.setIsolatedNode(nodeId);

    // Disable auto-following GPS camera to focus operator attention on the isolated target
    this.mapManager.setFollowLocation(false);

    // Center map on target node if coordinates are available
    const node = this.fusionEngine.getKnownNode(nodeId);
    if (node && typeof node.lat === 'number' && typeof node.lon === 'number' && (node.lat !== 0 || node.lon !== 0)) {
      const map = this.mapManager.getMap();
      if (map) {
        map.setView([node.lat, node.lon], 15);
      }
    }

    // Dismiss active popup card
    this.mapManager.closePopup();

    // Display floating tactical HUD reset badge
    const floatingBadge = document.getElementById('floating-isolation-badge');
    const isolatedNameEl = document.getElementById('isolated-node-name');
    if (floatingBadge && isolatedNameEl) {
      isolatedNameEl.textContent = displayName;
      isolatedNameEl.title = displayName;
      floatingBadge.style.display = 'inline-flex';
    }
  }

  /**
   * Resets active node isolation, restoring all packets across the mesh to the map.
   *
   * Why: Allows returning to full situational awareness with a single tap on the HUD badge.
   */
  private async clearNodeIsolation(): Promise<void> {
    await this.filterEngine.setIsolatedNode(null);

    const floatingBadge = document.getElementById('floating-isolation-badge');
    if (floatingBadge) {
      floatingBadge.style.display = 'none';
    }
  }

  /**
   * Sets up screen wake lock controls in the settings drawer and synchronizes
   * lock state with radio connection status and user preference.
   *
   * Why: Prevents smartphones from entering sleep/standby mode during active tracking sessions,
   * while allowing the screen to turn off normally to save battery when the radio is disconnected.
   */
  private setupWakeLockControls(): void {
    const toggleWakeLock = document.getElementById('toggle-wake-lock') as HTMLInputElement | null;
    const labelWakeLock = document.getElementById('label-wake-lock');

    if (!toggleWakeLock) return;

    const updateLabel = () => {
      if (!labelWakeLock) return;
      if (!this.wakeLockManager.isSupported()) {
        labelWakeLock.textContent = 'Display bei Verbindung aktiv halten (Nicht unterstützt)';
        labelWakeLock.style.opacity = '0.5';
      } else if (this.wakeLockManager.isActive()) {
        labelWakeLock.textContent = 'Display bei Verbindung aktiv halten (Aktiv: Standby aus)';
        labelWakeLock.style.opacity = '1';
      } else {
        labelWakeLock.textContent = 'Display bei Verbindung aktiv halten (Standby erlaubt)';
        labelWakeLock.style.opacity = '1';
      }
    };

    if (!this.wakeLockManager.isSupported()) {
      toggleWakeLock.disabled = true;
      toggleWakeLock.checked = false;
      updateLabel();
      return;
    }

    toggleWakeLock.checked = this.autoWakeLockEnabled;
    updateLabel();

    const WAKE_LOCK_PREF_KEY = 'noderadar_wake_lock_enabled';

    toggleWakeLock.addEventListener('change', async () => {
      this.autoWakeLockEnabled = toggleWakeLock.checked;
      localStorage.setItem(WAKE_LOCK_PREF_KEY, String(this.autoWakeLockEnabled));

      const radioState = this.radioClient.getState();
      const isConnected = radioState === 'CONNECTED' || radioState === 'SYNCING_NODEDB';

      if (this.autoWakeLockEnabled && isConnected) {
        await this.wakeLockManager.enable();
      } else {
        await this.wakeLockManager.disable();
      }
      updateLabel();
    });

    this.wakeLockManager.onStateChange(() => {
      updateLabel();
    });
  }

  /**
   * Sets up audio and haptic telemetry toggles in the settings drawer.
   *
   * Why: Enables eyes-on-the-road situational awareness during mobile field sessions.
   */
  private setupFieldErgonomicsControls(): void {
    if (typeof localStorage !== 'undefined') {
      localStorage.removeItem('noderadar_outdoor_mode');
    }
    if (typeof document !== 'undefined') {
      document.body.classList.remove('outdoor-mode');
    }

    const toggleAudio = document.getElementById('toggle-audio-feedback') as HTMLInputElement | null;
    const toggleHaptic = document.getElementById('toggle-haptic-feedback') as HTMLInputElement | null;

    if (toggleAudio) {
      toggleAudio.checked = this.feedbackManager.isAudioEnabled();
      toggleAudio.addEventListener('change', () => {
        this.feedbackManager.setAudioEnabled(toggleAudio.checked);
        showToast(toggleAudio.checked ? 'Audio-Feedback aktiviert' : 'Audio-Feedback deaktiviert', 'info');
      });
    }

    if (toggleHaptic) {
      toggleHaptic.checked = this.feedbackManager.isHapticEnabled();
      toggleHaptic.addEventListener('change', () => {
        this.feedbackManager.setHapticEnabled(toggleHaptic.checked);
        showToast(toggleHaptic.checked ? 'Haptisches Feedback aktiviert' : 'Haptisches Feedback deaktiviert', 'info');
      });
    }
  }

  private async refreshNodeSelectors(): Promise<void> {
    const nodes = this.fusionEngine.getAllKnownNodes();

    // Update node metrics if display element exists
    const metricNodes = document.getElementById('metric-nodes');
    if (metricNodes) {
      metricNodes.textContent = String(nodes.length);
    }
  }

  /**
   * Starts periodic Auto Traceroute transmissions targeting a specific node station.
   *
   * Why: Enables autonomous RF link quality benchmarking and RTT monitoring to a selected node
   * without cluttering the global settings drawer with target selectors.
   *
   * @param nodeId - 32-bit destination station ID
   * @param displayName - Station callsign or name for HUD indicator display
   */
  private startAutoTraceroute(nodeId: number, displayName: string): void {
    const txInterval = document.getElementById('tx-interval-input') as HTMLInputElement | null;
    const interval = Math.max(10, parseInt(txInterval?.value || '30', 10) || 30);

    this.activeTracerouteNodeName = displayName;
    this.tracerouteEngine.start(nodeId, interval);

    // Dismiss active popup card to maintain tactical map visibility
    this.mapManager.closePopup();

    // Display floating tactical Auto Traceroute HUD badge
    const badge = document.getElementById('floating-traceroute-badge');
    const nameEl = document.getElementById('traceroute-node-name');
    const intervalEl = document.getElementById('traceroute-badge-interval');
    if (badge && nameEl) {
      nameEl.textContent = displayName;
      nameEl.title = displayName;
      if (intervalEl) {
        intervalEl.textContent = `${interval}s`;
      }
      badge.style.display = 'inline-flex';
    }

    // Update status in settings drawer
    const statusEl = document.getElementById('traceroute-status-text');
    if (statusEl) {
      statusEl.textContent = `Aktiv: ${displayName} (${interval}s)`;
      statusEl.style.color = 'var(--accent-cyan)';
    }

    showToast(`Auto Traceroute gestartet für ${displayName} (${interval}s)`, 'info');
  }

  /**
   * Stops active Auto Traceroute polling and hides HUD badge.
   *
   * Why: Conserves channel bandwidth, respects LoRa duty limits, and clears HUD map overlays.
   */
  private stopAutoTraceroute(): void {
    this.tracerouteEngine.stop();
    this.activeTracerouteNodeName = null;

    const badge = document.getElementById('floating-traceroute-badge');
    if (badge) {
      badge.style.display = 'none';
    }

    const statusEl = document.getElementById('traceroute-status-text');
    if (statusEl) {
      statusEl.textContent = 'Inaktiv';
      statusEl.style.color = 'var(--text-primary)';
    }

    showToast('Auto Traceroute gestoppt', 'info');
  }

  /**
   * Sets up event listeners for Auto Traceroute triggers from node popups, HUD stop buttons,
   * runtime interval adjustments, and telemetry stats updates.
   *
   * Why: Replaces legacy Ping Loop drawer buttons with an intuitive map-marker workflow and settings synchronization.
   */
  private setupTracerouteControls(): void {
    const txInterval = document.getElementById('tx-interval-input') as HTMLInputElement | null;
    const btnStop = document.getElementById('btn-stop-traceroute');
    const metricAck = document.getElementById('metric-ack');
    const metricRtt = document.getElementById('metric-rtt');

    // Document delegation to handle popup button clicks from dynamically generated map markers
    document.addEventListener('click', async (e: MouseEvent) => {
      const autoBtn = (e.target as HTMLElement).closest('.btn-auto-traceroute-node') as HTMLElement | null;
      if (autoBtn) {
        const rawNum = autoBtn.getAttribute('data-node-num');
        const rawName = autoBtn.getAttribute('data-node-name') || 'Unbekannt';
        if (!rawNum) return;
        const nodeId = parseInt(rawNum, 10);
        if (isNaN(nodeId)) return;

        // Toggle off if already running on the same node, otherwise start for this target
        if (this.tracerouteEngine.isActive() && this.tracerouteEngine.getTargetNode() === (nodeId >>> 0)) {
          this.stopAutoTraceroute();
        } else {
          this.startAutoTraceroute(nodeId, rawName);
        }
        return;
      }

      const onceBtn = (e.target as HTMLElement).closest('.btn-traceroute-once-node') as HTMLElement | null;
      if (onceBtn) {
        const rawNum = onceBtn.getAttribute('data-node-num');
        const rawName = onceBtn.getAttribute('data-node-name') || 'Unbekannt';
        if (!rawNum) return;
        const nodeId = parseInt(rawNum, 10);
        if (isNaN(nodeId)) return;

        try {
          await this.tracerouteEngine.sendSingleTraceroute(nodeId);
          showToast(`Traceroute Ping an ${rawName} versendet`, 'info');
        } catch (err) {
          showToast(`Ping fehlgeschlagen: ${(err as Error).message}`, 'error');
        }
        return;
      }
    });

    if (btnStop) {
      btnStop.addEventListener('click', () => {
        this.stopAutoTraceroute();
      });
    }

    if (txInterval) {
      txInterval.addEventListener('change', () => {
        const val = Math.max(10, parseInt(txInterval.value, 10) || 30);
        txInterval.value = String(val);
        this.tracerouteEngine.setInterval(val);

        if (this.tracerouteEngine.isActive()) {
          const intervalEl = document.getElementById('traceroute-badge-interval');
          if (intervalEl) {
            intervalEl.textContent = `${val}s`;
          }
          const statusEl = document.getElementById('traceroute-status-text');
          if (statusEl && this.activeTracerouteNodeName) {
            statusEl.textContent = `Aktiv: ${this.activeTracerouteNodeName} (${val}s)`;
          }
        }
      });
    }

    this.tracerouteEngine.onStatsChange((stats: TracerouteStats) => {
      if (metricAck) {
        metricAck.textContent = `${stats.successRatePercent}%`;
      }
      if (metricRtt) {
        metricRtt.textContent = stats.lastRttMs !== null ? `RTT: ${stats.lastRttMs} ms` : 'RTT: -- ms';
      }
    });
  }

  private setupExportControls(): void {
    const btnGeo = document.getElementById('btn-export-geojson') as HTMLButtonElement;
    const btnCsv = document.getElementById('btn-export-csv') as HTMLButtonElement;
    const btnKml = document.getElementById('btn-export-kml') as HTMLButtonElement;

    btnGeo.addEventListener('click', async () => {
      const active = this.sessionManager.getActiveSession();
      await exportGeoJSON(active?.id);
      showToast('GeoJSON Export heruntergeladen', 'success');
    });

    btnCsv.addEventListener('click', async () => {
      const active = this.sessionManager.getActiveSession();
      await exportCSV(active?.id);
      showToast('CSV Export heruntergeladen', 'success');
    });

    btnKml.addEventListener('click', async () => {
      const active = this.sessionManager.getActiveSession();
      await exportKML(active?.id);
      showToast('KML Export heruntergeladen', 'success');
    });
  }

  /**
   * Refreshes the storage quota display in the drawer using navigator.storage.estimate().
   *
   * Why: Informs the operator of actual IndexedDB usage and remaining device storage space.
   */
  private async updateStorageQuotaDisplay(): Promise<void> {
    const quotaVal = document.getElementById('storage-quota-val');
    const persistVal = document.getElementById('storage-persisted-val');

    const estimate = await getStorageEstimate();
    if (estimate && quotaVal) {
      const usedStr = formatBytes(estimate.usageBytes);
      const totalStr = formatBytes(estimate.quotaBytes);
      quotaVal.textContent = `${usedStr} / ${totalStr} (${estimate.percentUsed}%)`;

      if (persistVal) {
        if (estimate.persisted) {
          persistVal.textContent = '● IndexedDB Persistenz aktiv';
          persistVal.style.color = 'var(--accent-green)';
        } else {
          persistVal.textContent = '○ Temporärer Speicher (Kein Persistenzschutz)';
          persistVal.style.color = 'var(--accent-yellow)';
        }
      }
    }
  }

  /**
   * Configures JSON database backup exports, portable file imports, and file input handlers.
   *
   * Why: Enables cross-device session transfers and guards against accidental browser data loss.
   */
  private setupBackupAndStorageControls(): void {
    const btnExportBackup = document.getElementById('btn-export-backup') as HTMLButtonElement | null;
    const btnImportBackup = document.getElementById('btn-import-backup') as HTMLButtonElement | null;
    const inputImport = document.getElementById('input-import-backup') as HTMLInputElement | null;

    if (btnExportBackup) {
      btnExportBackup.addEventListener('click', async () => {
        try {
          const active = this.sessionManager.getActiveSession();
          await exportDatabaseBackup(active?.id);
          showToast(`JSON Backup für "${active?.name || 'Datenbank'}" exportiert`, 'success');
        } catch (err) {
          showToast(`Backup-Export fehlgeschlagen: ${(err as Error).message}`, 'error');
        }
      });
    }

    if (btnImportBackup && inputImport) {
      btnImportBackup.addEventListener('click', () => {
        inputImport.click();
      });

      inputImport.addEventListener('change', async () => {
        const file = inputImport.files?.[0];
        if (!file) return;

        try {
          const text = await file.text();
          const result = await importDatabaseBackup(text);
          await this.refreshSessionList();
          await this.refreshNodeSelectors();
          await this.updateStorageQuotaDisplay();

          if (result.activeSessionId) {
            await this.sessionManager.switchSession(result.activeSessionId);
            await this.filterEngine.setSession(result.activeSessionId);
          } else {
            await this.filterEngine.applyFilters();
          }

          this.mapManager.renderAllKnownNodes(this.fusionEngine.getAllKnownNodes());

          showToast(
            `Import erfolgreich: ${result.packetsCount} Pakete, ${result.nodesCount} Stationen, ${result.sessionsCount} Session(s)`,
            'success',
            5000
          );
        } catch (err) {
          showToast(`Import fehlgeschlagen: ${(err as Error).message}`, 'error');
        } finally {
          inputImport.value = '';
        }
      });
    }

    this.updateStorageQuotaDisplay();
  }

  private setupDrawerToggles(): void {
    const btnToggleDrawer = document.getElementById('btn-toggle-drawer') as HTMLButtonElement | null;
    const controlDrawer = document.getElementById('control-drawer');

    if (btnToggleDrawer && controlDrawer) {
      btnToggleDrawer.addEventListener('click', () => {
        const isOpen = controlDrawer.classList.toggle('open');
        if (isOpen) {
          this.updateStorageQuotaDisplay();
        }
      });
    }

    this.trafficFeedController.setupDrawerToggles();
  }

  private setupTrafficFeed(): void {
    const metricPackets = document.getElementById('metric-packets');
    const metricRate = document.getElementById('metric-rate');
    const metricSnr = document.getElementById('metric-snr');
    const metricRssi = document.getElementById('metric-rssi');
    const countBadge = document.getElementById('traffic-count-badge');
    const metricRelays = document.getElementById('metric-relays');

    this.fusionEngine.onNodeUpdate(() => {
      this.feedbackManager.playNewNodeChime();
      this.feedbackManager.vibrate([45, 30, 60]);
    });

    this.fusionEngine.onFusedPacket((packet: StoredPacket) => {
      // Audio tone pitch-modulated by SNR & tactile pulse
      this.feedbackManager.playPacketTone(packet.snr);
      this.feedbackManager.vibrate(35);

      this.packetCount++;
      if (metricPackets) metricPackets.textContent = String(this.packetCount);
      if (countBadge) countBadge.textContent = `(${this.packetCount} Packets)`;

      // Rate calculation
      const elapsedMins = (Date.now() - this.startTime) / 60000;
      if (metricRate && elapsedMins > 0.1) {
        const rate = (this.packetCount / elapsedMins).toFixed(1);
        metricRate.textContent = `${rate} pkts/min`;
      }

      if (metricSnr) metricSnr.textContent = `${packet.snr} dB`;
      if (metricRssi) metricRssi.textContent = `${packet.rssi} dBm`;

      if (packet.relayNode && packet.relayNode !== 0) {
        this.knownRelays.add(packet.relayNode & 0xff);
        if (metricRelays) metricRelays.textContent = `${this.knownRelays.size} Relays identified`;
      }

      // Add to map if satisfies current filters
      if (this.filterEngine.matches(packet)) {
        this.mapManager.addPacket(packet);
      }

      // Delegate buffering, batching, and virtual DOM rendering to controller
      this.trafficFeedController.addPacket(packet);
    });
  }

  /**
   * Resets the in-memory packet buffer and clears traffic table rows.
   *
   * Why: Ensures clean state synchronization when active session is cleared or switched.
   */
  private clearTrafficFeed(): void {
    this.trafficFeedController.clear();
  }

  /**
   * Retrieves the in-memory buffered recent packets.
   *
   * Why: Enables test verification and diagnostic telemetry inspection.
   *
   * @returns Array of StoredPacket
   */
  getRecentPackets(): StoredPacket[] {
    return this.trafficFeedController.getRecentPackets();
  }
}
